import api from '@/api/api'

const state = {
    quotes: [],
    quoteItems: [],
    currentQuote: null,
    quoteById: null, // Add storage for full quote data
    loading: false,
    errors: {},
    pagination: {},
}

const getters = {
    quotes: state => state.quotes,
    currentQuote: state => state.currentQuote,
    quoteById: state => state.quoteById, // Add getter for full quote data
    loading: state => state.loading,
    errors: state => state.errors,
    pagination: state => state.pagination,
    quoteItems: state => state.quoteItems,
    
    // Get grouped quotes (quotes with their line items)
    groupedQuotes: (state) => {
        const quotesMap = new Map()
        
        state.quotes.forEach(quote => {
            if (!quotesMap.has(quote.quote_number)) {
                quotesMap.set(quote.quote_number, {
                    ...quote,
                    line_items: quote.line_items || []
                })
            }
        })
        
        return Array.from(quotesMap.values())
    },

    // Get quotes by status
    quotesByStatus: (state) => (status) => {
        return state.quotes.filter(quote => quote.status === status)
    },

    // Get quotes ready for conversion
    quotesReadyForOrder: (state) => {
        return state.quotes.filter(quote => {
            return quote.status === 'pending' && 
                   quote.all_items_available === true
        })
    },

     // Get all unique source branches used in quote items
    uniqueSourceBranches: state => {
        const branches = new Map()
        state.quoteItems.forEach(item => {
            if (item.source_branch_id && item.source_branch_name) {
                branches.set(item.source_branch_id, {
                    id: item.source_branch_id,
                    name: item.source_branch_name,
                    type: item.stock_source
                })
            }
        })
        return Array.from(branches.values())
    },
}

const mutations = {
    SET_QUOTES(state, quotes) {
        state.quotes = quotes.data || quotes
        if (quotes.meta) {
            state.pagination = quotes.meta
        }
    },
    
    SET_CURRENT_QUOTE(state, quote) {
        state.currentQuote = quote
    },

    SET_QUOTE_BY_ID(state, quote) {
        state.quoteById = quote
    },
    
    SET_LOADING(state, loading) {
        state.loading = loading
    },
    
    SET_ERRORS(state, errors) {
        state.errors = errors
    },
    
    SET_ERROR(state, error) {
        state.errors = { general: error }
    },
    
    UPDATE_QUOTE_STATUS(state, {quoteId, status}) {
        const quote = state.quotes.find(q => q.id === quoteId)
        if (quote) {
            quote.status = status
        }
        if (state.currentQuote && state.currentQuote.id === quoteId) {
            state.currentQuote.status = status
        }
    },
    
    UPDATE_QUOTE_LINE_ITEM(state, {quoteId, lineItemId, data}) {
        // Update in quotes list
        const quote = state.quotes.find(q => q.id === quoteId)
        if (quote && quote.line_items) {
            const lineItem = quote.line_items.find(li => li.id === lineItemId)
            if (lineItem) {
                Object.assign(lineItem, data)
            }
        }
        
        // Update in current quote
        if (state.currentQuote && state.currentQuote.id === quoteId) {
            const lineItem = state.currentQuote.line_items?.find(li => li.id === lineItemId)
            if (lineItem) {
                Object.assign(lineItem, data)
            }
        }
    },
    
    REMOVE_QUOTE_LINE_ITEM(state, {quoteId, lineItemId}) {
        // Remove from quotes list
        const quote = state.quotes.find(q => q.id === quoteId)
        if (quote && quote.line_items) {
            quote.line_items = quote.line_items.filter(li => li.id !== lineItemId)
        }
        
        // Remove from current quote
        if (state.currentQuote && state.currentQuote.id === quoteId && state.currentQuote.line_items) {
            state.currentQuote.line_items = state.currentQuote.line_items.filter(li => li.id !== lineItemId)
        }
    },
    
    REMOVE_QUOTE(state, quoteId) {
        state.quotes = state.quotes.filter(quote => quote.id !== quoteId)
        if (state.currentQuote && state.currentQuote.id === quoteId) {
            state.currentQuote = null
        }
    }
}

const actions = {

    async createQuote(_context, payload) {
        const response = await api.instance.post('/api/quotes', {
            request_entity_for: payload.request_entity_for,
            customer_id: payload.customer_id,
            vat_rate: payload.vat_rate,
            online: payload.online || false,
            is_backorder_quote: true,
            line_items: payload.quoteItems.map(item => {
                // Determine which price to use based on entity type
                const isWarehouse = payload.entity_type === 'warehouse';
                const unitPrice = isWarehouse ? parseFloat(item.branch_price || 0) : parseFloat(item.selling_price || 0);
                const unitPriceWithTax = isWarehouse ? parseFloat(item.branch_price_with_tax || 0) : parseFloat(item.selling_price_with_tax || 0);
                
                // Calculate line totals properly
                const quantity = parseInt(item.amount);
                const lineTotal = unitPrice * quantity;
                const lineTotalWithTax = unitPriceWithTax * quantity;
                
                return {
                    inventory_id: item.id,
                    requested_quantity: quantity,
                    notes: item.notes || '',
                    stock_source: isWarehouse ? 'warehouse' : 'branch',
                    unit_price: unitPrice.toFixed(2),
                    unit_price_with_tax: unitPriceWithTax.toFixed(2),
                    line_total: lineTotal.toFixed(2),
                    line_total_with_tax: lineTotalWithTax.toFixed(2)
                };
            })
        });
        return response.data;
    },

    // Fetch quotes with filters
    async fetchQuotes({commit}, params = {}) {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        try {
            // Include online_filter if provided
            const queryParams = {
                ...params,
                online_filter: params.online_filter !== undefined ? params.online_filter : 0,
                _t: Date.now(),
            };
            const response = await api.instance.get('/api/quotes', { params: queryParams })
            
            // Transform data to ensure line_items are properly structured
            const quotes = response.data.data || response.data
            const quoteList = Array.isArray(quotes) ? quotes : []
            const transformedQuotes = quoteList.map(quote => {
                const lineItems = quote.line_items || quote.lineItems || []
                return {
                    ...quote,
                    line_items: lineItems,
                    all_items_available: quote.all_items_available === true,
                    available_items_count: quote.available_items_count || 0,
                    ready_for_order: quote.ready_for_order === true,
                }
            })

            commit('SET_QUOTES', transformedQuotes)
            return transformedQuotes
        } catch (error) {
            console.error('Error fetching quotes:', error)
            commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch quotes')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Fetch single quote
    async fetchQuote({commit}, quoteId) {
        commit('SET_LOADING', true)
        try {
            const response = await api.instance.get(`/api/quotes/${quoteId}`)
            commit('SET_CURRENT_QUOTE', response.data)
            return response.data
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || {general: 'Failed to fetch quote'})
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async cleanupExpiredQuotes({commit}) {
        commit('SET_LOADING', true)
        try {
            const response = await api.instance.delete('/api/quotes/cleanup-expired')
            return response.data
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || {general: 'Failed to clean up expired quotes'})
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Delete quote
    async deleteQuote({commit}, quoteId) {
        commit('SET_LOADING', true)
        try {
            await api.instance.delete(`/api/quotes/${quoteId}`)
            commit('REMOVE_QUOTE', quoteId)
            return true
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || {general: 'Failed to delete quote'})
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Convert quote to order
    async convertQuoteToOrder({commit}, quoteId) {
        commit('SET_LOADING', true)
        try {
            const response = await api.instance.post(`/api/quotes/${quoteId}/convert-to-order`)
            commit('UPDATE_QUOTE_STATUS', {quoteId, status: 'converted'})
            return response.data
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || {general: 'Failed to convert quote'})
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Update line item
    async updateLineItem({ commit }, { quoteId, lineItemId, data }) {
        commit('SET_LOADING', true);
        try {
            const response = await api.instance.put(`/api/quotes/${quoteId}/line-items/${lineItemId}`, data);
            commit('UPDATE_QUOTE_LINE_ITEM', { quoteId, lineItemId, data });
            return response.data;
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || { general: 'Failed to update line item' });
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // Delete a line item from a quote
    async deleteLineItem({ commit }, { quoteId, lineItemId }) {
        commit('SET_LOADING', true);
        try {
            await api.instance.delete(`/api/quotes/${quoteId}/line-items/${lineItemId}`);
            commit('REMOVE_QUOTE_LINE_ITEM', { quoteId, lineItemId });
            return true;
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || { general: 'Failed to delete line item' });
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // Check availability for quote items
    async checkAvailability({commit}, {inventoryId, requestedQuantity}) {
        try {
            const response = await api.instance.post('/api/quotes/check-availability', {
                inventory_id: inventoryId,
                requested_quantity: requestedQuantity
            })
            return response.data
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || {general: 'Failed to check availability'})
            throw error
        }
    },

    // Get full quote data by ID (for printing)
    async get_full_quote_data_by_id({commit}, quoteId) {
        commit('SET_LOADING', true)
        try {
            const response = await api.instance.get(`/api/quotes/get_full_quote_data_by_id/${quoteId}`)
            commit('SET_QUOTE_BY_ID', response.data)
            return response.data
        } catch (error) {
            commit('SET_ERRORS', error.response?.data?.errors || {general: 'Failed to fetch full quote data'})
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}